import { Button, Input, Skeleton, Tag, Tooltip, Typography } from "antd";
import type { TextAreaRef } from "antd/es/input/TextArea";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type RefObject,
} from "react";
import { TbArrowUp, TbInfoCircle, TbSparkles, TbX } from "react-icons/tb";
import type {
  T_AssistantOffer,
  T_AssistantSuggestion,
  T_AssistantTurn,
  T_DisposableSource,
  T_OfferAssistantNegotiationContext,
} from "dtos/offer-assistant.dto";
import { ResultBlock } from "./OfferBlocks";
import { joinRefs, mentionShape, money, variantLabel, type T_SkeletonKind } from "utils/offer-assistant.utils";
import {
  OfferAssistantContext,
  useOfferAssistant,
  useOfferAssistantApi,
  type T_OfferAssistantApi,
} from "hooks/useOfferAssistant";

const { Text } = Typography;

type T_ComposerHandle = {
  focus(): void;
  insert(text: string): void;
};

type T_OfferAssistantProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Identity of the conversation — a new value starts / restores a different chat. */
  negotiationContext: T_OfferAssistantNegotiationContext;
  clientName: string;
  /** Header fallback until the first response's `session.due_amount` arrives. */
  dueAmount?: number;
  /** Header fallback until the first response's `session.disposable_source` arrives. */
  disposableSource?: T_DisposableSource;
};

// ─── Constants ────────────────────────────────────────────────────────────────

const DRAWER_WIDTH = 440;

const QUICK_ACTIONS = [
  { label: "Generate", text: "Generate new offers" },
  { label: "Variants", text: "Make variants of @" },
  { label: "Compare", text: "Compare @ and @" },
  { label: "Limits", text: "Show my limits" },
];

// ─── Small pieces ─────────────────────────────────────────────────────────────
function Mark({ pulsing, solid }: Readonly<{ pulsing?: boolean; solid?: boolean }>) {
  return (
    <span
      aria-hidden
      className={`relative isolate grid h-5 w-5 shrink-0 place-items-center overflow-hidden rounded-md ${
        solid ? "text-white" : "bg-violet-50 text-violet-500"
      } ${pulsing ? "animate-pulse" : ""}`}
    >
      {solid && <span className="mesh_bg" style={{ inset: -4 }} />}
      <TbSparkles className="h-3 w-3" />
    </span>
  );
}

function Suggestions({
  items,
  onPick,
  indent,
}: Readonly<{
  items: T_AssistantSuggestion[];
  onPick: (label: string) => void;
  indent?: boolean;
}>) {
  if (!items.length) return null;
  return (
    <div className={`flex flex-wrap gap-1.5 ${indent ? "pl-[30px]" : ""}`}>
      {items.map((s, i) => (
        <Button
          key={s.label}
          size="small"
          type={s.primary && i === 0 ? "primary" : "default"}
          className="!h-[30px] !px-3 !text-[12.5px] !font-medium"
          onClick={() => onPick(s.label)}
        >
          {s.label}
        </Button>
      ))}
    </div>
  );
}

// ─── Turns ────────────────────────────────────────────────────────────────────

/** `isLatest` — suggestion chips render only on the newest assistant turn; older ones are stale. */
function AssistantTurnView({ turn, isLatest }: { turn: T_AssistantTurn; isLatest: boolean }) {
  const { send } = useOfferAssistantApi();
  const isMessageOnly = turn.action === "clarify" || turn.action === "out_of_scope";
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start gap-2.5">
        <Mark />
        <div className="flex min-w-0 flex-col gap-1">
          <Text
            className={`text-sm leading-5 !text-gray-800 ${
              turn.action === "clarify" ? "border-0 border-l-2 border-solid border-violet-500 pl-2.5" : ""
            }`}
          >
            {turn.message}
          </Text>
          {turn.meta.restored_note && (
            <Text type="secondary" className="text-xs italic">
              {turn.meta.restored_note}
            </Text>
          )}
        </div>
      </div>
      <ResultBlock turn={turn} />
      {turn.meta.partial && (
        <Tooltip title={turn.meta.partial}>
          <div className="cursor-help self-start rounded-md border border-solid border-amber-200 bg-amber-50 px-2.5 py-1">
            <Text className="text-xs !text-amber-800">
              Only part of your request was handled. Ask again for the rest.
            </Text>
          </div>
        </Tooltip>
      )}
      {isLatest && <Suggestions items={turn.suggestions} onPick={send} indent={isMessageOnly} />}
    </div>
  );
}

function UserTurn({ text }: { text: string }) {
  return (
    <div className="flex justify-end">
      <Text className="max-w-[80%] whitespace-pre-wrap break-words rounded-xl rounded-br bg-violet-50 px-3 py-2 text-[13.5px] leading-5 !text-violet-800">
        {text}
      </Text>
    </div>
  );
}

function BlockSkeleton({ kind }: { kind: T_SkeletonKind }) {
  if (kind === "lines") return <Skeleton active title={false} paragraph={{ rows: 3, width: ["92%", "80%", "56%"] }} />;
  const rows = kind === "cards" ? 3 : 4;
  return (
    <div className="overflow-hidden rounded-xl border border-solid border-gray-200 bg-white">
      <div className="border-0 border-b border-solid border-gray-100 px-3.5 py-2.5">
        <Skeleton active title={{ width: "44%" }} paragraph={{ rows: 1, width: "62%" }} />
      </div>
      <div className="flex flex-col gap-2.5 p-3">
        {Array.from({ length: rows }, (_, i) =>
          kind === "cards" ? (
            <div key={i} className="flex flex-col gap-2.5 rounded-lg border border-solid border-gray-100 p-3.5">
              <Skeleton.Input active size="small" style={{ width: 80 }} />
              <Skeleton active title={false} paragraph={{ rows: 2, width: ["100%", "100%"] }} />
            </div>
          ) : (
            <Skeleton key={i} active title={false} paragraph={{ rows: 1, width: "100%" }} />
          ),
        )}
      </div>
    </div>
  );
}

/** Typing indicator, then — after 600ms — a skeleton shaped like the expected block. */
function PendingTurn({ guess }: { guess: T_SkeletonKind }) {
  const [showSkeleton, setShowSkeleton] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShowSkeleton(true), 600);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="flex flex-col gap-3" aria-live="polite" aria-busy="true">
      <div className="flex items-start gap-2.5">
        <Mark pulsing />
        <Text type="secondary" className="text-[13px] leading-5">
          Working on it…
        </Text>
      </div>
      {showSkeleton && <BlockSkeleton kind={guess} />}
    </div>
  );
}

function ErrorTurn({ title, message, onRetry }: { title: string; message?: string; onRetry?: () => void }) {
  return (
    <div
      role="alert"
      className="flex items-center gap-2.5 rounded-[10px] border border-solid border-red-200 bg-red-50 py-2.5 pl-3 pr-2.5"
    >
      <div className="flex min-w-0 flex-1 flex-col">
        <Text className="text-[13px] leading-[18px] !text-red-800">{title}</Text>
        {message && <Text className="text-xs !text-red-700">{message}</Text>}
      </div>
      {onRetry && (
        <Button size="small" danger onClick={onRetry}>
          Retry
        </Button>
      )}
    </div>
  );
}

// ─── Composer ─────────────────────────────────────────────────────────────────

type T_MentionState = { start: number; end: number; query: string };
type T_MentionOption = {
  ref: string;
  label: string;
  shape: string;
  isNested: boolean;
  isBalanced?: boolean;
  isRecommended?: boolean;
};

/** Base offers in order, each followed by its variants (indented). */
function buildMentionOptions(offers: T_AssistantOffer[]): T_MentionOption[] {
  return offers
    .filter((o) => o.kind === "base")
    .flatMap((b) => [
      { ref: b.ref, label: b.ref, shape: mentionShape(b.variables), isNested: false },
      ...offers
        .filter((v) => v.parent_ref === b.ref)
        .map((v) => ({
          ref: v.ref,
          label: variantLabel(v.ref),
          shape: mentionShape(v.variables),
          isNested: true,
          isBalanced: !!v.balanced,
          isRecommended: !!v.recommended,
        })),
    ]);
}

type T_ComposerProps = {
  value: string;
  onChange: (v: string) => void;
  onSend: (v: string) => void;
  busy: boolean;
  disabled: boolean;
  offers: T_AssistantOffer[];
  selected: string[];
  onRemoveSelected: (ref: string) => void;
  onCompare: () => void;
};

/** Auto-growing textarea, Enter to send / Shift+Enter newline, @-mention popover, quick chips, compare tray. */
const Composer = forwardRef<T_ComposerHandle, T_ComposerProps>(function Composer(p, ref) {
  const taRef = useRef<TextAreaRef>(null);
  const getTextArea = () => taRef.current?.resizableTextArea?.textArea ?? null;
  const [mention, setMention] = useState<T_MentionState | null>(null);
  const [active, setActive] = useState(0);
  const caretAfterUpdate = useRef<number | null>(null);

  const options = useMemo(() => buildMentionOptions(p.offers), [p.offers]);
  const filtered = mention
    ? options.filter((o) => o.ref.toLowerCase().includes(mention.query.trim().toLowerCase()))
    : [];
  const isMentionOpen = !!mention && filtered.length > 0;

  const detect = (text: string, caret: number) => {
    const m = /(^|\s)@([^@\n]{0,28})$/.exec(text.slice(0, caret));
    if (!m) {
      setMention(null);
      return;
    }
    setMention({ start: caret - m[2].length - 1, end: caret, query: m[2] });
    setActive(0);
  };

  const setText = (text: string, caret: number) => {
    caretAfterUpdate.current = caret;
    p.onChange(text);
  };

  // Restore caret after programmatic edits, and re-run @ detection (quick chips end in "@").
  useEffect(() => {
    const caret = caretAfterUpdate.current;
    const node = getTextArea();
    if (caret === null || !node) return;
    caretAfterUpdate.current = null;
    node.focus();
    node.setSelectionRange(caret, caret);
    detect(p.value, caret);
  }, [p.value]);

  const insertText = (text: string) => {
    const next = p.value.trim() ? `${p.value.replace(/\s+$/, "")} ${text}` : text;
    setText(next, next.length);
  };

  /** Quick actions replace the whole draft — one action at a time, never stacked. */
  const replaceText = (text: string) => {
    // Same text again: the value won't change, so the caret effect won't run — do it here.
    if (text === p.value) {
      const node = getTextArea();
      node?.focus();
      node?.setSelectionRange(text.length, text.length);
      detect(text, text.length);
      return;
    }
    setText(text, text.length);
  };

  useImperativeHandle(ref, () => ({ focus: () => getTextArea()?.focus(), insert: insertText }));

  const choose = (opt: T_MentionOption) => {
    if (!mention) return;
    const tail = p.value.slice(mention.end).replace(/^\s+/, "");
    const next = `${p.value.slice(0, mention.start)}${opt.ref} ${tail}`;
    setMention(null);
    setText(next, mention.start + opt.ref.length + 1);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (isMentionOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActive((a) => (a + 1) % filtered.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActive((a) => (a - 1 + filtered.length) % filtered.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        choose(filtered[active]);
        return;
      }
      if (e.key === "Escape") {
        // Close the popover only — don't let Esc bubble up and close the drawer.
        e.preventDefault();
        e.stopPropagation();
        setMention(null);
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      if (!p.busy && p.value.trim()) p.onSend(p.value);
    }
  };

  return (
    <div className="relative flex shrink-0 flex-col gap-2.5 border-0 border-t border-solid border-gray-200 bg-white px-4 pb-3.5 pt-2.5">
      {p.selected.length > 0 && (
        <div aria-label="Selected for compare" className="flex flex-wrap items-center gap-1.5">
          {p.selected.map((r) => (
            <Tag
              key={r}
              closable
              color="purple"
              bordered={false}
              className="!m-0 !text-xs !font-medium"
              onClose={(e) => {
                e.preventDefault();
                p.onRemoveSelected(r);
              }}
            >
              {r}
            </Tag>
          ))}
          <span className="flex-1" />
          <Button type="primary" size="small" disabled={p.selected.length < 2 || p.busy} onClick={p.onCompare}>
            Compare {p.selected.length}
          </Button>
        </div>
      )}

      <div className="relative flex flex-col gap-2 rounded-[10px] border border-solid border-gray-300 py-2 pl-1 pr-2 transition-shadow focus-within:border-violet-500 focus-within:shadow-[0_0_0_3px_#EDE9FE]">
        {isMentionOpen && (
          <div
            role="listbox"
            aria-label="Offers in this chat"
            className="absolute inset-x-0 bottom-[calc(100%+6px)] z-10 max-h-[330px] overflow-auto rounded-[10px] border border-solid border-gray-200 bg-white p-1.5 shadow-[0_10px_30px_rgba(17,24,39,.12)]"
          >
            <Text className="block px-2 pb-1 pt-1.5 text-[11.5px] !text-gray-400">Offers in this chat</Text>
            {filtered.map((o, i) => (
              <div
                key={o.ref}
                role="option"
                aria-selected={i === active}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(o);
                }}
                className={`flex cursor-pointer items-baseline gap-2.5 rounded-md py-1.5 pr-2 ${o.isNested ? "pl-6" : "pl-2"} ${
                  i === active ? "bg-violet-50" : ""
                }`}
              >
                <Text
                  className={`min-w-16 whitespace-nowrap text-[13px] ${o.isNested ? "font-medium" : "font-semibold"}`}
                >
                  {o.label}
                  {o.isRecommended && (
                    <span className="ml-1 text-xs text-amber-600" aria-label="Recommended">
                      ★
                    </span>
                  )}
                  {o.isBalanced && (
                    <span className="ml-1 text-xs text-teal-700" aria-label="Balanced">
                      ⚖
                    </span>
                  )}
                </Text>
                <Text type="secondary" className="truncate text-xs">
                  {o.shape}
                </Text>
              </div>
            ))}
          </div>
        )}

        <Input.TextArea
          ref={taRef}
          variant="borderless"
          value={p.value}
          disabled={p.disabled}
          placeholder="Ask about offers, or type @ to reference one"
          autoSize={{ minRows: 2, maxRows: 6 }}
          aria-label="Ask your doubts"
          className="!resize-none !px-2 !py-0.5 !text-sm !leading-5"
          onChange={(e) => {
            p.onChange(e.target.value);
            detect(e.target.value, e.target.selectionStart ?? e.target.value.length);
          }}
          onKeyDown={onKeyDown}
          onClick={(e) => detect(p.value, e.currentTarget.selectionStart ?? p.value.length)}
          onBlur={() => setMention(null)}
        />

        <div className="flex items-center gap-1.5 pl-2">
          {QUICK_ACTIONS.map((q) => (
            <Button
              key={q.label}
              size="small"
              disabled={p.disabled}
              className="!h-[26px] !px-2.5 !text-xs !font-medium !text-gray-600"
              onClick={() => replaceText(q.text)}
            >
              {q.label}
            </Button>
          ))}
          <span className="flex-1" />
          <Button
            type="primary"
            icon={<TbArrowUp />}
            loading={p.busy}
            disabled={p.disabled || (!p.busy && !p.value.trim())}
            aria-label="Send"
            onClick={() => p.onSend(p.value)}
          />
        </div>
      </div>
    </div>
  );
});

// ─── Conversation content (all chat state lives here) ─────────────────────────

type T_AssistantContentProps = Omit<T_OfferAssistantProps, "isOpen" | "onClose"> & {
  composerRef: RefObject<T_ComposerHandle>;
};

function AssistantContent({
  negotiationContext,
  clientName,
  dueAmount,
  disposableSource,
  composerRef,
}: T_AssistantContentProps) {
  const a = useOfferAssistant(negotiationContext);
  const scrollRef = useRef<HTMLDivElement>(null);
  const isNearBottom = useRef(true);
  const [showJump, setShowJump] = useState(false);

  const owed = a.session?.due_amount || dueAmount || 0;
  const latestAssistantId = useMemo(() => {
    for (let i = a.items.length - 1; i >= 0; i--) if (a.items[i].role === "assistant") return a.items[i].id;
    return undefined;
  }, [a.items]);
  const source = a.session?.disposable_source ?? disposableSource;
  const customCount =
    a.session?.custom_limits_count ?? a.latestContext?.limits.filter((l) => l.source === "custom").length ?? 0;

  const api = useMemo<T_OfferAssistantApi>(
    () => ({
      send: a.send,
      insert: (t) => composerRef.current?.insert(t),
      isSelected: (r) => a.selected.includes(r),
      toggleSelect: a.toggleSelect,
      owed,
      busy: a.busy,
    }),
    [a.send, a.selected, a.toggleSelect, a.busy, owed, composerRef],
  );

  // Auto-scroll to the newest turn, unless the user has scrolled up — then offer "Jump to latest".
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (!isNearBottom.current) {
      setShowJump(true);
      return;
    }
    const turns = el.querySelectorAll<HTMLElement>("[data-turn]");
    const lastTurn = turns[turns.length - 1];
    const lastUser = [...turns].reverse().find((t) => t.dataset.turn === "user");
    const target = lastUser ?? lastTurn;
    // If the latest exchange is taller than the viewport, pin the user's message at the top instead.
    el.scrollTop =
      target && target.offsetHeight + (lastTurn?.offsetHeight ?? 0) > el.clientHeight
        ? target.offsetTop - 16
        : el.scrollHeight;
  }, [a.items, a.startStatus]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    isNearBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 64;
    if (isNearBottom.current) setShowJump(false);
  };

  const jumpToLatest = () => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    setShowJump(false);
  };

  return (
    <OfferAssistantContext.Provider value={api}>
      {/* ── Client meta ─────────────────────────────────────────────────────── */}
      <div className="flex shrink-0 flex-row w-full justify-between gap-1.5 border-0 border-b border-solid border-gray-200 px-4 py-2.5">
        <div className="flex items-center gap-1.5">
          <Text type="secondary" className="truncate text-[12.5px]">
            {clientName}
          </Text>
          <Text aria-hidden className="!text-gray-300">
            ·
          </Text>
          <Text type="secondary" className="whitespace-nowrap text-[12.5px] tabular-nums">
            {a.startStatus === "loading" ? null : `${money(owed)} owed`}
          </Text>
          {source === "bank_fallback" && (
            <Tooltip title="Affordability is estimated from bank statements">
              <TbInfoCircle
                tabIndex={0}
                aria-label="Affordability is estimated from bank statements"
                className="shrink-0 cursor-help text-gray-400"
              />
            </Tooltip>
          )}
        </div>
        {customCount > 0 && (
          <Tag
            color="purple"
            bordered={false}
            className="!m-0 cursor-pointer self-start !rounded-full !text-[11.5px] !font-medium"
            onClick={() => a.send("Show my current limits")}
          >
            Custom limits: {customCount}
          </Tag>
        )}
      </div>

      {/* ── Conversation ────────────────────────────────────────────────────── */}
      <div className="relative flex min-h-0 flex-1 flex-col">
        <div
          ref={scrollRef}
          onScroll={onScroll}
          aria-live="polite"
          className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-4 pb-7 pt-5"
        >
          {a.startStatus === "missing" && (
            <Text type="secondary" className="text-center text-[13px]">
              Missing loan details — the assistant needs an appkey, loan_id to start.
            </Text>
          )}
          {a.startStatus === "loading" && <PendingTurn guess="cards" />}
          {a.startStatus === "error" && <ErrorTurn title="Couldn't load this conversation." onRetry={a.retryStart} />}

          {a.items.map((it) => {
            switch (it.role) {
              case "user":
                return (
                  <div key={it.id} data-turn="user">
                    <UserTurn text={it.text} />
                  </div>
                );
              case "assistant":
                return (
                  <div key={it.id} data-turn="assistant">
                    <AssistantTurnView turn={it.turn} isLatest={it.id === latestAssistantId} />
                  </div>
                );
              case "pending":
                return (
                  <div key={it.id} data-turn="pending">
                    <PendingTurn guess={it.guess} />
                  </div>
                );
              case "error":
                return (
                  <div key={it.id} data-turn="error">
                    {it.restored ? (
                      <ErrorTurn title="This request failed." message={it.message} />
                    ) : (
                      <ErrorTurn
                        title={
                          it.text
                            ? "Couldn't reach the offer engine. Your message wasn't sent."
                            : "Couldn't start the assistant."
                        }
                        message={it.message}
                        onRetry={() => a.retry(it)}
                      />
                    )}
                  </div>
                );
            }
          })}
        </div>

        {showJump && (
          <button
            type="button"
            style={{ transform: "translateX(-50%)" }}
            onClick={jumpToLatest}
            className="absolute bottom-3 left-1/2 h-7  cursor-pointer rounded-full border border-solid border-gray-200 bg-white px-3 text-xs font-medium text-gray-700 shadow-[0_4px_12px_rgba(17,24,39,.08)]"
          >
            Jump to latest ↓
          </button>
        )}
      </div>

      <Composer
        ref={composerRef}
        value={a.draft}
        onChange={a.setDraft}
        onSend={a.send}
        busy={a.busy}
        disabled={a.startStatus !== "ready"}
        offers={a.offers}
        selected={a.selected}
        onRemoveSelected={a.toggleSelect}
        onCompare={() => a.send(`Compare ${joinRefs(a.selected)}`)}
      />
    </OfferAssistantContext.Provider>
  );
}

// ─── Drawer ───────────────────────────────────────────────────────────────────

/**
 * Suggestions chat, rendered in a right-side antd Drawer.
 * The drawer keeps its content mounted after close, so the conversation survives close → reopen.
 * Changing appkey / loan_id / thread_id remounts the content and loads that chat instead.
 */
function OfferAssistant({ isOpen, onClose, negotiationContext, ...rest }: T_OfferAssistantProps) {
  const composerRef = useRef<T_ComposerHandle>(null);
  const { application_id, loan_id, thread_id } = negotiationContext;

  // return (
  //   <Drawer
  //     open={isOpen}
  //     onClose={onClose}
  //     placement="right"
  //     width={DRAWER_WIDTH}
  //     title={
  //       <div className="flex items-center gap-2">
  //         <Mark solid />
  //         <Text strong className="text-sm">
  //           Suggestions
  //         </Text>
  //       </div>
  //     }
  //     afterOpenChange={(open) => {
  //       if (open) composerRef.current?.focus();
  //     }}
  //     styles={{
  //       body: { padding: 0, display: "flex", flexDirection: "column", overflow: "hidden" },
  //     }}
  //   >
  //     <AssistantContent
  //       key={`${application_id ?? ""}:${loan_id ?? ""}:${thread_id ?? ""}`}
  //       negotiationContext={negotiationContext}
  //       composerRef={composerRef}
  //       {...rest}
  //     />
  //   </Drawer>
  // );
  return (
    <aside
      aria-hidden={!isOpen}
      style={{
        width: isOpen ? DRAWER_WIDTH : 0,
        minWidth: isOpen ? DRAWER_WIDTH : 0,
        transition: "width 0.2s ease, min-width 0.2s ease",
      }}
      className="h-full flex flex-col bg-white border-0 border-l border-solid border-gray-200 overflow-hidden shrink-0"
    >
      <div style={{ width: DRAWER_WIDTH }} className="h-full flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-0 border-b border-solid border-gray-100 shrink-0">
          <div className="flex items-center gap-2">
            <Mark solid />
            <Text strong className="text-sm">
              Suggestions
            </Text>
          </div>
          <Button onClick={onClose} type="text" size="small" icon={<TbX />} />
        </div>

        {/* ── Body ───────────────────────────────────────────────────────── */}
        <AssistantContent
          key={`${application_id ?? ""}:${loan_id ?? ""}:${thread_id ?? ""}`}
          negotiationContext={negotiationContext}
          composerRef={composerRef}
          {...rest}
        />
      </div>
    </aside>
  );
}

export default OfferAssistant;
